import { InformationModal, DefaultPrimaryButton } from "@/styles/globalstyles";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import CloseButton from "../buttons/CloseButton";
import { useState } from "react";
import {
  useCreateOtherCostProcess,
  useDeleteOtherCostProcess,
  useGetOtherCostProcess,
  useUpdateOtherCostProcess,
} from "@/hooks/materialSupplierHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { getAxiosErrorMessage } from "@/utils/healper";
import type {
  OtherCostProcessPayload,
  OtherCostProcess,
} from "@/api/materialSupplierService";
import { Add, Edit, Delete } from "@mui/icons-material";
import Swal from "sweetalert2";

interface ManageProcessModalProps {
  open: boolean;
  handleClose: () => void;
  materialId: number;
}

const ManageProcessModal = ({
  open,
  handleClose,
  materialId,
}: ManageProcessModalProps) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<OtherCostProcessPayload>({
    material_supplier_id: materialId,
    column_name: "",
    display_order: 0,
  });

  const {
    data: OtherCostProcess,
    isFetching: loadingOtherCostProcess,
    isError: errorOtherCostProcess,
  } = useGetOtherCostProcess(materialId);

  const {
    mutate: handleAdd,
    isPending: creating,
    isError: isErrorCreating,
    error: errorCreating,
  } = useCreateOtherCostProcess();

  const {
    mutate: handleEdit,
    isPending: editing,
    isError: isErrorEditing,
    error: errorEditing,
  } = useUpdateOtherCostProcess();

  const {
    mutate: handleDelete,
    isPending: deleting,
    isError: isErrorDeleting,
    error: errorDeleting,
  } = useDeleteOtherCostProcess();

  useQueryHandler({
    isError: errorOtherCostProcess,
    isFetching: loadingOtherCostProcess,
  });

  useQueryHandler({
    isFetching: creating,
    isError: isErrorCreating,
    error: getAxiosErrorMessage(errorCreating),
  });

  useQueryHandler({
    isFetching: editing,
    isError: isErrorEditing,
    error: getAxiosErrorMessage(errorEditing),
  });

  useQueryHandler({
    isFetching: deleting,
    isError: isErrorDeleting,
    error: getAxiosErrorMessage(errorDeleting),
  });

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.column_name) {
      newErrors.column_name = "กรุณากรอกชื่อ";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      material_supplier_id: materialId,
      column_name: "",
      display_order: 0,
    });
    setErrors({});
    setIsAdding(false);
    setEditingId(null);
  };

  const onClose = () => {
    resetForm();
    handleClose();
  };

  const handleAddClick = () => {
    resetForm();
    setIsAdding(true);
  };

  const handleEditClick = (process: OtherCostProcess) => {
    setFormData({
      material_supplier_id: materialId,
      column_name: process.column_name,
      display_order: process.display_order,
    });
    setEditingId(process.id);
    setIsAdding(false);
  };

  const handleSave = () => {
    if (!validateForm()) return;

    if (editingId) {
      handleEdit(
        {
          material_supplier_id: materialId,
          column_id: editingId,
          column_name: formData.column_name,
          display_order: formData.display_order,
        },
        {
          onSuccess: () => {
            resetForm();
          },
        }
      );
    } else {
      const maxOrder =
        OtherCostProcess?.reduce(
          (max, process) => Math.max(max, process.display_order || 0),
          0
        ) || 0;

      handleAdd(
        { ...formData, display_order: maxOrder + 1 },
        {
          onSuccess: () => {
            resetForm();
          },
        }
      );
    }
  };

  const handleDeleteClick = (id: number) => {
    Swal.fire({
      title: "คุณต้องการลบ Process นี้หรือไม่?",
      text: "การลบ Process จะส่งผลกระทบต่อการคำนวนราคา",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDelete({
          material_supplier_id: materialId,
          column_id: id,
        });
      }
    });
  };

  const handleFieldChange = (
    field: keyof OtherCostProcessPayload,
    value: string | number
  ) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxHeight: "90vh" }}>
        <CloseButton onClick={onClose} />
        <Typography variant="h2">{"จัดการ Process"}</Typography>
        <Box p={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box></Box>
            {!isAdding && !editingId && (
              <Button
                variant="outlined"
                sx={DefaultPrimaryButton}
                onClick={handleAddClick}
                startIcon={<Add />}
              >
                เพิ่ม Process
              </Button>
            )}
          </Box>
          {isAdding && !editingId && (
            <Box
              mb={3}
              p={2}
              border="2px solid #2196f3"
              borderRadius={2}
              bgcolor="#f8f9fa"
            >
              <Typography variant="subtitle1" mb={2} color="primary">
                เพิ่ม Process ใหม่
              </Typography>

              <Box display="flex" gap={2} alignItems="start">
                <Box flex={1}>
                  <TextField
                    fullWidth
                    label="ชื่อ Process"
                    value={formData.column_name}
                    onChange={(e) =>
                      handleFieldChange("column_name", e.target.value)
                    }
                    error={!!errors.column_name}
                    helperText={errors.column_name}
                    size="small"
                  />
                </Box>
                <Box display="flex" gap={1}>
                  <Button variant="outlined" onClick={resetForm} size="small">
                    ยกเลิก
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={creating || editing}
                    size="small"
                  >
                    เพิ่ม
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
          <Box>
            {OtherCostProcess && OtherCostProcess.length > 0 ? (
              OtherCostProcess.map((process) => {
                if (editingId === process.id) {
                  return (
                    <Box
                      key={process.id}
                      mb={1}
                      p={2}
                      border="2px solid #ff9800"
                      borderRadius={2}
                      bgcolor="#fff3e0"
                    >
                      <Typography
                        variant="subtitle2"
                        mb={2}
                        color="#f57c00"
                        fontWeight={600}
                      >
                        แก้ไข Process
                      </Typography>

                      <Box display="flex" gap={2} alignItems="start">
                        <Box flex={1}>
                          <TextField
                            fullWidth
                            label="ชื่อ Process"
                            value={formData.column_name}
                            sx={{ bgcolor: "white" }}
                            onChange={(e) =>
                              handleFieldChange("column_name", e.target.value)
                            }
                            error={!!errors.column_name}
                            helperText={errors.column_name}
                            size="small"
                          />
                        </Box>
                        <Box display="flex" gap={1}>
                          <Button
                            variant="outlined"
                            onClick={resetForm}
                            size="small"
                            sx={{ bgcolor: "white" }}
                          >
                            ยกเลิก
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={creating || editing}
                            size="small"
                            sx={{
                              bgcolor: "#f57c00",
                              "&:hover": { bgcolor: "#e65100" },
                            }}
                          >
                            บันทึก
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  );
                }

                return (
                  <Box
                    key={process.id}
                    mb={1}
                    p={2}
                    border="1px solid #e0e0e0"
                    borderRadius={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      "&:hover": {
                        bgcolor: "#f5f5f5",
                      },
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {process.column_name}
                      </Typography>
                    </Box>

                    {!isAdding && !editingId && (
                      <Box display="flex" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(process)}
                          color="primary"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(process.id)}
                          color="error"
                          disabled={deleting}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                );
              })
            ) : (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">ไม่มี Process</Typography>
                {!isAdding && (
                  <Button
                    variant="outlined"
                    onClick={handleAddClick}
                    sx={{ mt: 2 }}
                    startIcon={<Add />}
                  >
                    เพิ่ม Process
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ManageProcessModal;
